import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth";
import { useCart } from "../cart";
import {
  REACT_APP_BRAND_NAME,
  currency,
  getDiscount,
  getShippingCost,
  getTaxAmount,
  placeholderImg,
} from "../shared";

function MiniCartDrawer({ onClose }) {
  const { state, setQty, removeFromCart } = useCart();
  const items = Object.entries(state.items);
  const subtotal = items.reduce(
    (sum, [key, it]) => sum + it.unitPrice * it.qty,
    0,
  );
  const discount = getDiscount(subtotal, state.coupon);
  const shippingCost = getShippingCost(
    subtotal - discount,
    state.shipping,
    state.coupon,
  );
  const taxable = Math.max(0, subtotal - discount);
  const tax = getTaxAmount(taxable, state.shipping?.state);
  const total = Math.max(0, taxable + tax + shippingCost);

  return (
    <div className="jh-drawer-overlay" onClick={onClose}>
      <aside className="jh-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="jh-drawer-head">
          <div className="jh-drawer-title">Your Cart</div>
          <button className="jh-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {!items.length ? (
          <div className="jh-empty">Your cart is empty.</div>
        ) : (
          <>
            <ul className="jh-drawer-list">
              {items.map(([key, it]) => (
                <li key={key} className="jh-drawer-item">
                  <img
                    className="jh-drawer-img"
                    src={it.image || placeholderImg}
                    alt={it.name}
                    onError={(e) => {
                      e.currentTarget.src = placeholderImg;
                    }}
                  />
                  <div className="jh-drawer-main">
                    <div className="jh-drawer-name">{it.name}</div>
                    {it.metal && (
                      <div className="jh-drawer-meta">Metal: {it.metal}</div>
                    )}
                    {it.stoneType && (
                      <div className="jh-drawer-meta">
                        Stone: {it.stoneType}
                      </div>
                    )}
                    {it.size != null && (
                      <div className="jh-drawer-meta">Size: {it.size}</div>
                    )}
                    <div className="jh-drawer-price">
                      {currency(it.unitPrice)}
                    </div>
                  </div>
                  <div className="jh-drawer-qty">
                    <input
                      className="jh-input"
                      type="number"
                      min={0}
                      value={it.qty}
                      onChange={(e) =>
                        setQty(key, Math.max(0, Number(e.target.value) || 0))
                      }
                    />
                    <button
                      className="jh-link danger"
                      onClick={() => removeFromCart(key)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="jh-drawer-summary">
              <div className="jh-cart-row">
                <span>Subtotal</span>
                <strong>{currency(subtotal)}</strong>
              </div>
              {state.coupon && (
                <div className="jh-cart-row">
                  <span>Coupon ({state.coupon.code})</span>
                  <strong>-{currency(discount)}</strong>
                </div>
              )}
              <div className="jh-cart-row">
                <span>Est. tax</span>
                <strong>{currency(tax)}</strong>
              </div>
              <div className="jh-cart-row">
                <span>Shipping</span>
                <strong>{currency(shippingCost)}</strong>
              </div>
              <div className="jh-cart-row total">
                <span>Total</span>
                <strong>{currency(total)}</strong>
              </div>
              <div className="jh-actions">
                <Link
                  to="/cart"
                  className="jh-btn jh-btn--ghost"
                  onClick={onClose}
                >
                  View cart
                </Link>
                <Link to="/checkout" className="jh-btn" onClick={onClose}>
                  Checkout
                </Link>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function SiteHeader() {
  const { state } = useCart();
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const itemCount = Object.values(state.items).reduce(
    (sum, it) => sum + it.qty,
    0,
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`jh-header${scrolled ? " jh-header--scrolled" : ""}`}>
      <div className="jh-header-inner">
        <div className="jh-header-left" />
        <Link to="/" className="jh-brand jh-brand--center">
          {REACT_APP_BRAND_NAME}
        </Link>
        <nav className="jh-nav jh-nav--right">
          <Link to="/" className="jh-navlink">
            Home
          </Link>

          <button
            className="jh-navlink jh-quick"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open mini cart"
          >
            Quick View
          </button>
          <Link to="/cart" className="jh-cart">
            Cart <span className="jh-badge">{itemCount}</span>
          </Link>
          {user ? (
            <>
              <span className="jh-navlink">
                Hi, {user.username || user.first || user.email || user.id}
              </span>
              <button className="jh-navlink" onClick={logout}>
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="jh-navlink">
                Login
              </Link>
              <Link to="/register" className="jh-navlink">
                Register
              </Link>
            </>
          )}
        </nav>
      </div>
      {drawerOpen && <MiniCartDrawer onClose={() => setDrawerOpen(false)} />}
    </header>
  );
}

export default SiteHeader;
